import React from 'react';
import classNames from 'classnames';
import { Button } from '@schibsted-svp/react-ui';

import { Thumbnail } from '../../Thumbnail/Thumbnail';
import { getItemDataType } from '../helpers';
import { formatDuration } from '../../../utils';

import type {
  DataType,
  EnrichedTag,
  PresetElement,
  SvpAsset,
  SvpCategory,
} from '../../../types';
import type { FC } from 'react';

import styles from './PresetItem.module.scss';

const getElementImageUrl = (item: PresetElement, itemDataType?: string) => {
  switch (itemDataType) {
    case 'category': {
      const category = item as SvpCategory;
      return category.additional
        ? `${category.additional.image}?t[]=161x91q80`
        : undefined;
    }
    case 'asset': {
      const asset = item as SvpAsset;
      return asset.images ? `${asset.images.main}?t[]=161x91q80` : undefined;
    }
    case 'story': {
      const story = item as EnrichedTag;
      return story.images ? `${story.images.main}?t[]=180q80` : undefined;
    }
    case 'profile': {
      const profile = item as EnrichedTag;
      return profile.images ? `${profile.images.main}?t[]=180q80` : undefined;
    }
  }
};

const getElementLabels = (item: PresetElement, itemDataType?: string) => {
  const labels: string[] = [];
  switch (itemDataType) {
    case 'category': {
      const category = item as SvpCategory;
      labels.push(category.isSeries ? 'Series' : 'Category');
      if (!category.showCategory) {
        labels.push('Archived');
      }
      break;
    }
    case 'asset': {
      const asset = item as SvpAsset;
      labels.push(asset.series ? 'Episode' : 'Asset');
      if (asset.category?.title) {
        labels.push(asset.category.title);
      }
      if (asset.streamType !== 'live') {
        labels.push(formatDuration(asset.duration ?? 0));
      } else {
        labels.push('Live');
      }
      if (!asset.category?.showCategory || asset.status !== 'active') {
        labels.push('Archived');
      }
      break;
    }
    case 'story': {
      labels.push('Story');
      break;
    }
    case 'profile': {
      labels.push('Profile');
      break;
    }
  }

  return labels.join(' • ');
};

type Props = {
  item: PresetElement;
  dataType: DataType;
  onClick: (item: PresetElement) => void;
  isDraggable?: boolean;
  isSelected?: boolean;
};

export const PresetItem: FC<Props> = ({
  item,
  dataType,
  onClick,
  isDraggable = false,
  isSelected = false,
}) => {
  const itemDataType = getItemDataType(item, dataType);
  const imageUrl = getElementImageUrl(item, itemDataType);
  const labels = getElementLabels(item, itemDataType);

  const title = item.title;

  const hasImage = imageUrl !== undefined;

  return (
    <div
      className={classNames(styles.container, {
        [styles.draggable]: isDraggable,
        [styles.selected]: isSelected,
        [styles.disabled]: !hasImage,
      })}
      onClick={!isDraggable ? () => onClick(item) : undefined}
      title={!hasImage ? 'No image' : undefined}
      data-testid={`preset-result-${item.id}`}
    >
      <div className={styles.details}>
        <div
          className={classNames(styles.thumbnail, {
            [styles.noImage]: !hasImage,
          })}
        >
          {imageUrl && (
            <Thumbnail
              url={imageUrl}
              isRound={dataType === 'profiles'}
              aspectRatio={dataType === 'stories' ? '9/16' : undefined}
            />
          )}
        </div>
        <div className={styles.title}>
          {title}
          {labels && <div className={styles.subtitle}>{labels}</div>}
        </div>
      </div>
      <Button
        type="button"
        variant="standard"
        size="compact"
        className={styles.button}
        onClick={isDraggable ? () => onClick(item) : undefined}
        data-testid={`preset-item-button-${item.id}`}
      >
        {isSelected ? '× Remove' : '+ Add'}
      </Button>
    </div>
  );
};
